import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useGeneratePublicUrl } from '@/hooks/useGenerations';
import { 
  Download, 
  Edit3, 
  RefreshCw, 
  Clock, 
  Target, 
  BarChart3, 
  FileText,
  Copy,
  Share2,
  ChevronDown,
  FileCode,
  FileSpreadsheet,
  Star
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
interface SurveyStatisticsSidebarProps {
  config: any;
  generatedQuestions: any[];
  answers: Record<string, any>;
  surveyTemplateId?: string;
  surveyTemplate?: any;
  currentGeneration?: any;
  isGenerating: boolean;
  mode: string;
  toolId: string;
  currentLang: string;
  onGenerateQuestions: () => void;
  onCopyAllQuestions: () => void;
  onExportQuestions: (format: 'json' | 'csv') => void;
}

const SurveyStatisticsSidebar: React.FC<SurveyStatisticsSidebarProps> = ({
  config,
  generatedQuestions,
  answers,
  surveyTemplateId,
  surveyTemplate,
  currentGeneration,
  isGenerating,
  mode,
  toolId,
  currentLang,
  onGenerateQuestions,
  onCopyAllQuestions,
  onExportQuestions
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const generatePublicUrl = useGeneratePublicUrl();
  const { t } = useTranslation();
  const getQuestionBreakdown = () => {
    const breakdown: Record<string, number> = {};
    generatedQuestions.forEach(q => {
      const category = q.category || q.type || 'General';
      breakdown[category] = (breakdown[category] || 0) + 1;
    });
    return breakdown;
  };

  const getEstimatedTime = () => {
    return Math.ceil(generatedQuestions.length * 0.75);
  };

  return (
    <div className="p-8 bg-muted/20 h-full">
      <div className="sticky top-6 space-y-6">
        {/* Configuration Summary */}
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Target className="w-4 h-4 text-primary" />
            {surveyTemplateId ? t('results.surveyTemplateResults.sidebar.title') : t('results.surveyTemplateResults.sidebar.configuration')}
          </h3>
          
          {surveyTemplateId && surveyTemplate && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-primary mb-2">
                <Star className="w-4 h-4" />
                <span className="text-sm font-medium">{t('results.surveyTemplateResults.sidebar.usingTemplate')}</span>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-primary/70 font-medium mb-1">{t('results.surveyTemplateResults.sidebar.templateName')}</div>
                  <div className="text-sm text-primary font-medium">{surveyTemplate.name}</div>
                </div>
                <div>
                  <div className="text-xs text-primary/70 font-medium mb-1">{t('results.surveyTemplateResults.sidebar.category')}</div>
                  <div className="text-sm text-primary capitalize">{surveyTemplate.category}</div>
                </div>
                <div>
                  <div className="text-xs text-primary/70 font-medium mb-1">{t('results.surveyTemplateResults.sidebar.targetAudience')}</div>
                  <div className="text-sm text-primary">{surveyTemplate.target_audience}</div>
                </div>
                <div>
                  <div className="text-xs text-primary/70 font-medium mb-1">{t('results.surveyTemplateResults.sidebar.estimatedDuration')}</div>
                  <div className="text-sm text-primary">{surveyTemplate.estimated_duration_minutes} minutes</div>
                </div>
                <div>
                  <div className="text-xs text-primary/70 font-medium mb-1">{t('results.surveyTemplateResults.sidebar.description')}</div>
                  <div className="text-xs text-primary/80 line-clamp-3">{surveyTemplate.description}</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Only show configuration details if NOT using a survey template */}
          {!surveyTemplateId && (
            <div className="space-y-3 mb-6">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.companySize')}</div>
                <div className="font-medium">{config.companySize}</div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.industry')}</div>
                <div className="font-medium">{config.industry}</div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.targetAudience')}</div>
                <div className="font-medium">{config.targetAudience}</div>
              </div>
            </div>
          )}
          
          {/* Action Buttons - Only show if NOT using a survey template */}
          {!surveyTemplateId && (
            <div className="space-y-3 mb-6">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/${currentLang}/tools/${toolId}/configuration`)}
                className="w-full"
                size="sm"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                {t('results.surveyTemplateResults.sidebar.modifyConfiguration')}
              </Button>
              <Button 
                onClick={onGenerateQuestions}
                disabled={isGenerating}
                className="w-full"
                size="sm"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('results.surveyTemplateResults.sidebar.regenerating')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('results.surveyTemplateResults.sidebar.regenerateQuestions')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
        
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-primary" />
            {t('results.surveyTemplateResults.sidebar.exportAndShare')}
          </h4>
          <div className="space-y-3 mb-6">
            <Button onClick={onCopyAllQuestions} variant="outline" size="sm" className="w-full justify-start">
              <Copy className="w-4 h-4 mr-2" />
              {t('results.surveyTemplateResults.sidebar.copyAllQuestions')}
            </Button>
            <Button 
              onClick={() => {
                if (currentGeneration) {
                  generatePublicUrl.mutate(currentGeneration.id, {
                    onSuccess: (updatedGeneration) => {
                      const url = `${window.location.origin}/${currentLang}/share/${updatedGeneration.public_url_slug}`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: t('results.surveyTemplateResults.sidebar.toasterLinkGeneratedAndCopied'),
                        description: t('results.surveyTemplateResults.sidebar.toasterPublicLinkCopied'),
                      });
                    }
                  });
                } else {
                  toast({
                    title: t('results.surveyTemplateResults.sidebar.toasterSaveGenerationFirst'),
                    description: t('results.surveyTemplateResults.sidebar.toasterPleaseSaveGeneration'),
                    variant: "destructive",
                  });
                }
              }} 
              variant="outline" 
              size="sm" 
              className="w-full justify-start"
              disabled={!currentGeneration || generatePublicUrl.isPending}
            >
              <Share2 className="w-4 h-4 mr-2" />
              {generatePublicUrl.isPending ? t('results.surveyTemplateResults.sidebar.generating') : t('results.surveyTemplateResults.sidebar.sharePublicLink')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Download className="w-4 h-4 mr-2" />
                  {t('results.surveyTemplateResults.sidebar.exportSurvey')}
                  <ChevronDown className="w-4 h-4 ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onExportQuestions('json')}>
                  <FileCode className="w-4 h-4 mr-2" />
                  {t('results.surveyTemplateResults.sidebar.exportAsJSON')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExportQuestions('csv')}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {t('results.surveyTemplateResults.sidebar.exportAsCSV')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            {t('results.surveyTemplateResults.sidebar.surveyStatistics')}
          </h3>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{t('results.surveyTemplateResults.sidebar.estimatedTime')}</span>
              </div>
              <div className="text-2xl font-bold text-primary">{getEstimatedTime()} min</div>
              <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.basedOnQuestionCount')}</div>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{t('results.surveyTemplateResults.sidebar.focusAreas')}</span>
              </div>
              <div className="text-2xl font-bold text-primary">{config.focusAreas.length}</div>
              <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.areasCovered')}</div>
            </div>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{t('results.surveyTemplateResults.sidebar.questions')}</span>
              </div>
              <div className="text-2xl font-bold text-primary">{generatedQuestions.length}</div>
              <div className="text-xs text-muted-foreground">{t('results.surveyTemplateResults.sidebar.totalGenerated')}</div>
            </div>
          </div>
        </div>
        
        <div>
          <h4 className="font-medium mb-3">{t('results.surveyTemplateResults.sidebar.questionTypes')}</h4>
          <div className="space-y-2">
            {Object.entries(getQuestionBreakdown()).map(([type, count]) => (
              <div key={type} className="bg-white rounded p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{type}</span>
                  <Badge variant="secondary" className="text-xs">{count}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="font-medium mb-3">{t('results.surveyTemplateResults.sidebar.testProgress')}</h4>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-muted-foreground mb-2">
              {t('results.surveyTemplateResults.sidebar.answered')}: {Object.keys(answers).length} / {generatedQuestions.length}
            </div>
            <Progress 
              value={(Object.keys(answers).length / generatedQuestions.length) * 100} 
              className="h-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyStatisticsSidebar; 